import { useMutation, useQuery, useQueryClient } from 'react-query'
import { deleteTemplate, getAllDeletedTemplates, getAllTemplates, getDeletedTemplate, getTemplate, restoreTemplate, saveTemplate } from './templateApi'
import { TemplateState } from './templateModel'
import { notification } from 'antd'

export const useTemplate = (id: number, isDeleted: boolean) => {
  return useQuery(['templates', id], {
    queryFn: () => isDeleted ? getDeletedTemplate(id) : getTemplate(id),
    enabled: !!id
  })
}

type SaveTemplateRequest = {
  id: number,
  changeSet: TemplateState
}

export const useSaveTemplateMutation = (onSuccess: (id: number) => void) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({id, changeSet}: SaveTemplateRequest) => saveTemplate(id, changeSet),
    onSuccess: x => {
      queryClient.invalidateQueries(['templates', x.id])
      queryClient.invalidateQueries(['allTemplates'])
      onSuccess(x.id)
    },
    onError: () => {
      notification.error({message: 'There was an error saving this template. Please try again later.'})
    }
  })
}

export const useDeleteTemplateMutation = (onSuccess: () => void) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => deleteTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['allTemplates'])
      onSuccess()
    }
  })
}

export const useRestoreTemplateMutation = (onSuccess: () => void) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => restoreTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['allTemplates'])
      onSuccess()
    }
  })
}

export const useAllTemplatesQuery = (deleted: boolean, enabled: boolean = true) => {
  return useQuery(['allTemplates', deleted], () => deleted ? getAllDeletedTemplates() : getAllTemplates(), {
    refetchOnWindowFocus: false,
    enabled
  })
}

export const useAllTemplateQueryInvalidator = () => {
  const queryClient = useQueryClient()
  return { 
    invalidate: () => queryClient.invalidateQueries('allTemplates')
  }
}
